import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/system';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { GridActionsCell } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';
import React, {
    Children,
    Fragment,
    isValidElement,
    memo,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { DataGridActionsContext } from './DataGridActionsContext';
import { useDataGridActions } from './hooks/useDataGridActions';

const CONTAINER_SX: SxProps<Theme> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: '0 8px'
    }
};

function DataGridActionsCellComponent(props: GridRenderCellParams) {
    const {
        entityName,
        feature,
        activeRow,
        isRowValid,
        onEdit,
        onShare,
        enableInlineEditing,
        enableInlineDelete,
        onDelete,
        onDeleteHandle,
        copyTooltipTitle,
        onCopy,
        onActivateOrInactivateHandle,
        customActions,
        customRowActions,
        isMobile,
        actionLabels,
        handleEditClick,
        handleEditCommit,
        handleEditCancel,
        handleDeleteClick,
        hideEditActionWhenInlineEditing
    } = useContext(DataGridActionsContext);

    const rowDetails = useMemo(() => ({ activeRow, isRowValid }), [activeRow, isRowValid]);

    const editHandlers = useMemo(
        () => ({
            onEdit,
            enableInlineEditing,
            handleEditClick,
            handleEditCommit,
            handleEditCancel
        }),
        [onEdit, enableInlineEditing, handleEditClick, handleEditCommit, handleEditCancel]
    );

    const deleteHandlers = useMemo(
        () => ({
            enableInlineDelete,
            onDelete,
            onDeleteHandle,
            handleDeleteClick
        }),
        [enableInlineDelete, onDelete, onDeleteHandle, handleDeleteClick]
    );

    const copyHandlers = useMemo(
        () => ({
            onCopy,
            copyTooltipTitle
        }),
        [onCopy, copyTooltipTitle]
    );

    const actions = useDataGridActions({
        params: props,
        feature,
        entityName,
        onActivateOrInactivateHandle,
        rowDetails,
        editHandlers,
        onShare,
        deleteHandlers,
        copyHandlers,
        customActions,
        customRowActions,
        isMobile,
        actionLabels,
        hideEditActionWhenInlineEditing
    });

    const [hovered, setHovered] = useState<boolean>(false);

    const ref = useRef<HTMLDivElement>(null);

    /**
     * Tracks row hover state by attaching event listeners to the parent DataGrid row element.
     * This enables performance optimization by only rendering action buttons when the row is hovered,
     * reducing the number of DOM elements and improving overall grid performance.
     *
     * Event listeners are attached to the row element rather than this component to ensure
     * hover detection works reliably across the entire row width, not just the actions cell.
     */
    useEffect(() => {
        const el = ref.current?.closest('.MuiDataGrid-row');

        if (!el) return;

        const onMouseEnter = () => setHovered(true);
        const onMouseLeave = () => setHovered(false);

        el.addEventListener('mouseenter', onMouseEnter);
        el.addEventListener('mouseleave', onMouseLeave);

        return () => {
            el.removeEventListener('mouseenter', onMouseEnter);
            el.removeEventListener('mouseleave', onMouseLeave);
        };
    }, []);

    /**
     * Many actions can be conditionally rendered as empty fragments based on permissions or other factors.
     * This memoized value checks if there are any actionable buttons to render, preventing unnecessary
     * rendering of the GridActionsCell component when there are no visible actions.
     */
    const hasActionsToRender = useMemo(
        () =>
            actions.some((action) => {
                if (!isValidElement<{ children?: ReactNode }>(action)) {
                    return false;
                }

                const isEmptyFragment = action.type === Fragment && Children.count(action.props?.children) === 0;

                return !isEmptyFragment;
            }),
        [actions]
    );

    const shouldShowActions = (props.hasFocus || hovered) && hasActionsToRender;

    return (
        <Box ref={ref} sx={CONTAINER_SX}>
            {shouldShowActions ? <GridActionsCell {...props}>{actions}</GridActionsCell> : null}
        </Box>
    );
}

export const DataGridActionsCell = memo(DataGridActionsCellComponent);
DataGridActionsCell.displayName = 'DataGridActionsCell';
